import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createStripeSubscription, getPaymentFailurePresentation, getUserBillingPage, PaymentFailureModal } from '@wegic/price-ui';
import { Message } from '@wegic/ui';
import '@wegic/ui/dist/components/Message/index.css';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { handleSubscriptionAndPointsAPI } from '@/components/PayModal/handle';
import StripeCheckoutDrawer from '@/components/StripeCheckoutDrawer';
import { markFirstGenerationContinuation } from '@/helpers/firstGenerationEntry';
import { buildContinueGenerationAutoInput, buildGenerationReference } from '@/helpers/generationReferenceImage';
import { resumePaidLaunchOrderGenerationAfterLogin } from '@/helpers/launchOrderGeneration';
import { openLoginModal } from '@/helpers/loginModal';
import { getAnonymousCashierAttributionPayload } from '@/helpers/pay/anonymousCashierAttribution';
import { hasShownPrepayRetention, markPrepayRetentionShown } from '@/helpers/pay/prepayRetentionStorage';
import { handleAnonymousPaymentCompletion } from '@/helpers/payBeforeLoginFlow';
import { collectUpdatePaymentMethodCta } from '@/helpers/payment/updatePaymentMethodTracking';
import { createPaymentSuccessWaiter, waitForPaymentSuccessOrRefresh } from '@/helpers/paymentCheckout';
import { collect } from '@/modules/Collect/helper';
import globalEventEmitter from '@/modules/EventEmitter';
import { EVENTS } from '@/modules/EventEmitter/const';
import { createAnonymousTrialCashierSession, getAnonymousLatestPaidEmail } from '@/services/pay';
import { removePlanKey } from '@/services/pay/storage';
import { updateUserNotify } from '@/services/user';
import { UserNotify } from '@/services/user/const';
import { useAutoInputStore } from '@/stores/autoInput';
import { useI18nStore } from '@/stores/i18n';
import { useProjectStore } from '@/stores/project';
import { useUserStore } from '@/stores/user';
import { useUserNotifyStore } from '@/stores/userNotify';
import CheckoutLeftPanel from '../Markdown/MarkdownViewer/CustomTagCom/GenStartButton/CheckoutLeftPanel';
import { TRIAL_PAY_PRODUCT } from './const';
import { canOpenTrialPayFlow, createTraceId, getDuration, isPayBeforeLoginTrialPayment, runTrialPaymentContinuation } from './helper';
const PaymentRetentionModal = /*#__PURE__*/ lazy(()=>import('../Markdown/MarkdownViewer/CustomTagCom/GenStartButton/PaymentRetentionModal'));
const HOSTED_TRIAL_PAYMENT_KEY = 'wegic:hosted-trial-payment';
function sendGenerationMessage() {
    let projectId = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : useProjectStore.getState().projectId, context = arguments.length > 1 ? arguments[1] : void 0;
    const continueLabel = intl.get('common.continue.generating');
    // The chat payment CTA is shown after Reference UI confirmation. Rebuild
    // the continuation from the active project so the confirmed image and
    // generation identity survive Stripe checkout just like the launch panel.
    const autoInput = buildContinueGenerationAutoInput(continueLabel, context === null || context === void 0 ? void 0 : context.order, projectId);
    if ((context === null || context === void 0 ? void 0 : context.ctaSurface) === 'launch_panel' && context.order && projectId) {
        const reference = buildGenerationReference(context.order);
        markFirstGenerationContinuation(projectId, 'style', reference);
    }
    useAutoInputStore.getState().setContent(autoInput.content, autoInput.files, autoInput.displayContent, projectId);
}
function collectTrialPaymentSuccess(context) {
    collect('prepay_payment_success', {
        rights: 'prepay_generate',
        plan: 'prepay',
        bill_reason: 'prepay',
        credits_left: useUserStore.getState().userPoints,
        cta_surface: (context === null || context === void 0 ? void 0 : context.ctaSurface) ?? 'chat'
    });
}
export default function GlobalTrialPayModal() {
    const currentLanguage = useI18nStore((state)=>state.currentLanguage);
    const projectId = useProjectStore((state)=>state.projectId);
    const updateNotifyState = useUserNotifyStore((state)=>state.updateNotifyState);
    const [modalVisible, setModalVisible] = useState(false);
    const [stripeOptions, setStripeOptions] = useState(null);
    const [showCheckout, setShowCheckout] = useState(false);
    const [retentionModalVisible, setRetentionModalVisible] = useState(false);
    const [shouldMountRetentionModal, setShouldMountRetentionModal] = useState(false);
    const [isWaitingForWebSocket, setIsWaitingForWebSocket] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [paymentFailure, setPaymentFailure] = useState(null);
    const cleanupRef = useRef(null);
    const openingRef = useRef(false);
    const paymentSuccessRef = useRef(false);
    const modalVisibleRef = useRef(false);
    const showCheckoutRef = useRef(false);
    const isLoadingRef = useRef(false);
    const isWaitingForWebSocketRef = useRef(false);
    const openTraceIdRef = useRef(null);
    const openStartTimeRef = useRef(null);
    const iframeStageStartTimeRef = useRef(null);
    const flowContextRef = useRef(undefined);
    const hostedReturnHandledRef = useRef(false);
    useEffect(()=>{
        modalVisibleRef.current = modalVisible;
    }, [
        modalVisible
    ]);
    useEffect(()=>{
        showCheckoutRef.current = showCheckout;
    }, [
        showCheckout
    ]);
    useEffect(()=>{
        isLoadingRef.current = isLoading;
    }, [
        isLoading
    ]);
    useEffect(()=>{
        isWaitingForWebSocketRef.current = isWaitingForWebSocket;
    }, [
        isWaitingForWebSocket
    ]);
    const resetTrialPayFlow = useCallback(()=>{
        if (cleanupRef.current) {
            cleanupRef.current();
        }
        openingRef.current = false;
        paymentSuccessRef.current = false;
        modalVisibleRef.current = false;
        showCheckoutRef.current = false;
        isLoadingRef.current = false;
        isWaitingForWebSocketRef.current = false;
        openTraceIdRef.current = null;
        openStartTimeRef.current = null;
        iframeStageStartTimeRef.current = null;
        flowContextRef.current = undefined;
        removePlanKey();
        setStripeOptions(null);
        setIsWaitingForWebSocket(false);
        setIsLoading(false);
        setRetentionModalVisible(false);
        setModalVisible(false);
        setShowCheckout(false);
    }, []);
    const completePayment = useCallback(async function() {
        let waitForWebSocket = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : true;
        const flowContext = flowContextRef.current;
        paymentSuccessRef.current = true;
        if (isPayBeforeLoginTrialPayment(flowContext)) {
            collectTrialPaymentSuccess(flowContext);
            const pendingProjectId = flowContext === null || flowContext === void 0 ? void 0 : flowContext.projectId;
            resetTrialPayFlow();
            if (!pendingProjectId) {
                openLoginModal({
                    dismissible: false
                });
                return;
            }
            await handleAnonymousPaymentCompletion({
                projectId: pendingProjectId,
                order: flowContext.order
            }, {
                queryLatestPaidEmail: getAnonymousLatestPaidEmail,
                isLogin: ()=>useUserStore.getState().isLogin,
                openLogin: (options)=>openLoginModal({
                        ...options,
                        header: {
                            title: intl.get('payBeforeLogin.paymentSuccess.title'),
                            subtitle: intl.get('payBeforeLogin.paymentSuccess.subtitle')
                        }
                    }),
                resume: resumePaidLaunchOrderGenerationAfterLogin
            });
            return;
        }
        isWaitingForWebSocketRef.current = true;
        setIsWaitingForWebSocket(true);
        try {
            if (waitForWebSocket) {
                const paymentWaiter = createPaymentSuccessWaiter();
                cleanupRef.current = paymentWaiter.cancel;
                await waitForPaymentSuccessOrRefresh(paymentWaiter);
            } else {
                await handleSubscriptionAndPointsAPI();
            }
            collectTrialPaymentSuccess(flowContext);
            if ((flowContext === null || flowContext === void 0 ? void 0 : flowContext.ctaSurface) === 'launch_panel') {
                const activeProjectId = useProjectStore.getState().projectId;
                if (!flowContext.projectId || activeProjectId !== flowContext.projectId) {
                    return;
                }
            }
            const acknowledged = await runTrialPaymentContinuation({
                continueGeneration: ()=>{
                    sendGenerationMessage(flowContext === null || flowContext === void 0 ? void 0 : flowContext.projectId, flowContext);
                },
                acknowledgeEntitlement: async ()=>{
                    await updateUserNotify(UserNotify.TrafficPackage);
                    updateNotifyState(UserNotify.TrafficPackage, false);
                }
            });
            if (!acknowledged) {
                console.warn('[GlobalTrialPayModal] Failed to acknowledge trial payment entitlement');
            }
        } finally{
            resetTrialPayFlow();
        }
    }, [
        resetTrialPayFlow,
        updateNotifyState
    ]);
    const handleCancel = useCallback(()=>{
        if (isWaitingForWebSocketRef.current) {
            return;
        }
        if (!projectId || !showCheckoutRef.current || paymentSuccessRef.current || hasShownPrepayRetention(projectId)) {
            resetTrialPayFlow();
            return;
        }
        markPrepayRetentionShown(projectId);
        collect('prepay_payment_retention_show');
        setShouldMountRetentionModal(true);
        setRetentionModalVisible(true);
    }, [
        projectId,
        resetTrialPayFlow
    ]);
    const handleContinuePayment = useCallback(()=>{
        setRetentionModalVisible(false);
    }, []);
    const handleGiveUpDiscount = useCallback(()=>{
        resetTrialPayFlow();
    }, [
        resetTrialPayFlow
    ]);
    const handleIframeReady = useCallback(()=>{
        const duration = getDuration(iframeStageStartTimeRef.current);
        const traceId = openTraceIdRef.current;
        setShouldMountRetentionModal(true);
        if (duration !== null && traceId) {
            collect('prepay_payment_open_duration', {
                stage: 'iframe_ready',
                duration,
                trace_id: traceId
            });
        }
        iframeStageStartTimeRef.current = null;
    }, []);
    const openPayFlow = useCallback(async (context)=>{
        if (!canOpenTrialPayFlow({
            isLoading: isLoadingRef.current,
            isOpening: openingRef.current,
            isCheckoutVisible: modalVisibleRef.current || showCheckoutRef.current
        })) {
            globalEventEmitter.emit(EVENTS.TRIAL_PAY_FLOW_LOADING, false);
            return;
        }
        openingRef.current = true;
        setPaymentFailure(null);
        flowContextRef.current = context;
        isLoadingRef.current = true;
        setIsLoading(true);
        try {
            const traceId = createTraceId();
            openTraceIdRef.current = traceId;
            openStartTimeRef.current = performance.now();
            iframeStageStartTimeRef.current = null;
            localStorage.setItem('SelectPlanKey', 'prepay');
            paymentSuccessRef.current = false;
            const returnUrl = window.location.href;
            const useAnonymousCashier = isPayBeforeLoginTrialPayment(context);
            const session = useAnonymousCashier ? await createAnonymousTrialCashierSession({
                ...getAnonymousCashierAttributionPayload(),
                lineItem: {
                    productId: TRIAL_PAY_PRODUCT.id,
                    priceId: TRIAL_PAY_PRODUCT.prices[0].id,
                    quantity: 1
                },
                successUrl: returnUrl,
                cancelUrl: returnUrl,
                language: currentLanguage,
                redirectOnCompletion: 'NEVER'
            }) : await createStripeSubscription({
                product: TRIAL_PAY_PRODUCT,
                price: TRIAL_PAY_PRODUCT.prices[0],
                successUrl: returnUrl,
                cancelUrl: returnUrl,
                language: currentLanguage,
                domain: '',
                subdomain: '',
                siteId: '',
                isHostCheckout: true
            });
            const prepareDuration = getDuration(openStartTimeRef.current);
            if (prepareDuration !== null) {
                collect('prepay_payment_open_duration', {
                    stage: 'prepare',
                    duration: prepareDuration,
                    trace_id: traceId
                });
            }
            if (session.url) {
                try {
                    sessionStorage.setItem(HOSTED_TRIAL_PAYMENT_KEY, JSON.stringify({
                        context,
                        sessionId: session.sessionId
                    }));
                } catch  {
                // Storage may be unavailable in private browsing; payment itself can proceed.
                }
                collect('prepay_payment_modal_open');
                openingRef.current = false;
                isLoadingRef.current = false;
                setIsLoading(false);
                window.location.assign(session.url);
                return;
            }
            if (!session.clientSecret) {
                throw new Error('Invalid response: missing clientSecret or url');
            }
            const stripeOpts = {
                fetchClientSecret: async ()=>session.clientSecret,
                onComplete: completePayment
            };
            // Only show the embedded drawer after the backend confirms this session
            // does not have a Hosted Checkout URL.
            modalVisibleRef.current = true;
            setModalVisible(true);
            setStripeOptions(stripeOpts);
            collect('prepay_payment_modal_open');
            iframeStageStartTimeRef.current = performance.now();
            showCheckoutRef.current = true;
            setShowCheckout(true);
            globalEventEmitter.emit(EVENTS.TRIAL_PAY_FLOW_LOADING, false);
            openingRef.current = false;
            isLoadingRef.current = false;
            setIsLoading(false);
        } catch (error) {
            const failure = getPaymentFailurePresentation(error);
            openTraceIdRef.current = null;
            openStartTimeRef.current = null;
            iframeStageStartTimeRef.current = null;
            openingRef.current = false;
            isLoadingRef.current = false;
            resetTrialPayFlow();
            globalEventEmitter.emit(EVENTS.TRIAL_PAY_FLOW_LOADING, false);
            if (failure.checkoutDisplay === 'tip') {
                Message.error(intl.get(failure.messageKey));
            } else {
                setPaymentFailure(failure);
                collectUpdatePaymentMethodCta('show', 'credits_checkout');
            }
        }
    }, [
        completePayment,
        currentLanguage,
        projectId,
        resetTrialPayFlow
    ]);
    useEffect(()=>{
        globalEventEmitter.on(EVENTS.OPEN_TRIAL_PAY_MODAL, openPayFlow);
        return ()=>{
            globalEventEmitter.off(EVENTS.OPEN_TRIAL_PAY_MODAL, openPayFlow);
        };
    }, [
        openPayFlow
    ]);
    useEffect(()=>{
        var _storedState_context;
        if (hostedReturnHandledRef.current || typeof window === 'undefined') {
            return;
        }
        const url = new URL(window.location.href);
        const sessionId = url.searchParams.get('session_id');
        if (!sessionId) {
            return;
        }
        let storedState = null;
        try {
            const rawState = window.sessionStorage.getItem(HOSTED_TRIAL_PAYMENT_KEY);
            if (rawState) {
                storedState = JSON.parse(rawState);
            }
        } catch  {
            storedState = null;
        }
        if (!storedState || storedState.sessionId !== sessionId) {
            return;
        }
        if (((_storedState_context = storedState.context) === null || _storedState_context === void 0 ? void 0 : _storedState_context.projectId) && !projectId) {
            return;
        }
        hostedReturnHandledRef.current = true;
        flowContextRef.current = storedState.context;
        window.sessionStorage.removeItem(HOSTED_TRIAL_PAYMENT_KEY);
        url.searchParams.delete('session_id');
        window.history.replaceState(window.history.state, document.title, url.toString());
        void completePayment(false);
    }, [
        completePayment,
        projectId
    ]);
    useEffect(()=>{
        return ()=>{
            if (cleanupRef.current) {
                cleanupRef.current();
            }
        };
    }, []);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(StripeCheckoutDrawer, {
                visible: modalVisible,
                onCancel: handleCancel,
                stripeOptions: stripeOptions,
                showCheckout: showCheckout,
                isWaitingForPayment: isWaitingForWebSocket,
                leftPanel: /*#__PURE__*/ _jsx(CheckoutLeftPanel, {}),
                onIframeReady: handleIframeReady,
                onRetry: ()=>collect('stripe_checkout_retry', {
                        checkout_type: '299'
                    }),
                onRetryShow: (params)=>collect('stripe_checkout_retry_show', {
                        ...params,
                        checkout_type: '299'
                    })
            }),
            paymentFailure && /*#__PURE__*/ _jsx(PaymentFailureModal, {
                visible: true,
                failure: paymentFailure,
                onCancel: ()=>setPaymentFailure(null),
                onUpdatePaymentMethod: ()=>{
                    collectUpdatePaymentMethodCta('click', 'credits_checkout');
                    getUserBillingPage();
                }
            }),
            (shouldMountRetentionModal || retentionModalVisible) && /*#__PURE__*/ _jsx(Suspense, {
                fallback: null,
                children: /*#__PURE__*/ _jsx(PaymentRetentionModal, {
                    visible: retentionModalVisible,
                    onCancel: handleContinuePayment,
                    onContinuePayment: handleContinuePayment,
                    onGiveUpDiscount: handleGiveUpDiscount
                })
            })
        ]
    });
}
